import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  CachedStorage,
  StorageOptions,
  StorageType,
  StoredItem,
  STORAGE_KEYS,
} from './storage.types';
import { EncryptedData, BaseEncryptedStorage } from 'utilities';
import { LoginResponse } from 'angular-auth-oidc-client';

/**
 * Application secret used for storage encryption key derivation.
 * In production, this should come from environment configuration.
 */
export const APP_STORAGE_SECRET = 'keystone-app-secret-v1';

/**
 * Expected shape of OIDC token claims.
 * Extend this interface as new claims are needed.
 */
export interface TokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  [key: string]: unknown;
}

/**
 * Encrypted storage service using Web Crypto API for secure localStorage operations.
 * Uses AES-GCM encryption with PBKDF2 key derivation.
 *
 * All items are cached in memory and stored as a single encrypted chunk in localStorage.
 * Only predefined keys from STORAGE_KEYS are allowed.
 */
@Injectable({
  providedIn: 'root',
})
export class EncryptedStorageService extends BaseEncryptedStorage {
  private readonly SALT_KEY_PREFIX = '__encrypted_storage_salt_';
  private readonly DATA_KEY_PREFIX = '__encrypted_storage_data_';
  private readonly ANONYMOUS_USER_ID = '__anonymous__';

  /**
   * Dev-mode only: Maps hashed user IDs to original user IDs for debugging.
   * Static so it persists across service instances during a session.
   */
  private static readonly userIdMap = new Map<string, string>();

  private userId: string | null = null;
  private hashedUserId: string | null = null;

  /** In-memory caches of all stored items, separated by storage type */
  private localCache: CachedStorage = {};
  private sessionCache: CachedStorage = {};

  /** Track if caches have unsaved changes */
  private isLocalDirty = false;
  private isSessionDirty = false;

  /**
   * Allowed storage keys. Use registerKey() to add new keys at runtime.
   */
  private readonly allowedKeys = new Set<string>(Object.values(STORAGE_KEYS));

  /**
   * Reflects whether the storage is fully initialized for the current user.
   * Set to true after decryption key derivation and cache loading complete,
   * reset to false on lock() or reset().
   */
  private readonly _initialized$ = new BehaviorSubject<boolean>(false);
  readonly initialized$ = this._initialized$.asObservable();

  /**
   * Initialize the encryption with a password/passphrase and optional user identifier.
   * Must be called before using setItem or getItem.
   * Each user gets their own salt and namespaced storage keys.
   * If no userId is provided, data is stored under an anonymous namespace.
   * The userId is hashed with environment info before being used as localStorage key.
   */
  async initialize(password: string, userId?: string): Promise<void> {
    this.userId = userId ?? this.ANONYMOUS_USER_ID;

    // Hash the userId with environment info for localStorage key
    const envSuffix = this.getEnvInfo();
    const userIdWithEnv = this.userId + envSuffix;
    this.hashedUserId = await this.hashUserId(userIdWithEnv);

    // Track user mapping in dev mode for debugging
    if (isDevMode()) {
      EncryptedStorageService.userIdMap.set(this.hashedUserId, this.userId);
    }

    const saltKey = `${this.SALT_KEY_PREFIX}${this.hashedUserId}__`;
    const salt = this.getOrCreateSalt(saltKey);
    await this.initializeCrypto(password, salt);

    // Load existing data into caches for both storage types
    await this.loadCache('local');
    await this.loadCache('session');
    this._initialized$.next(true);
  }

  /**
   * Initialize encrypted storage for the authenticated user.
   * Called automatically on successful authentication.
   */
  async initializeStorageForUser(response: LoginResponse): Promise<void> {
    const userData = response.userData;
    const userEmail = this.getStringClaim(userData, 'email');
    const userId = this.getStringClaim(userData, 'sub');

    if (!userId) {
      console.warn('No user ID (sub) in token claims, cannot initialize encrypted storage');
      return;
    }

    // Derive encryption password from user ID and app secret
    // This ensures consistent encryption across sessions while being user-specific
    const encryptionPassword = `${APP_STORAGE_SECRET}:${userId}`;

    // Use email as the storage namespace (or userId if email not available)
    const storageNamespace = userEmail ?? userId;

    // Lock existing storage if initialized for a different user to prevent data corruption
    if (this.isInitialized()) {
      await this.lock();
    }

    await this.initialize(encryptionPassword, storageNamespace);
    if (isDevMode()) {
      console.log(`Encrypted storage initialized for user: ${storageNamespace}`);
    }
  }
  /**
   * Check if the current session is for an anonymous (non-logged-in) user.
   */
  isAnonymous(): boolean {
    return this.userId === this.ANONYMOUS_USER_ID;
  }

  /**
   * Check if the service has been initialized with a password.
   */
  isInitialized(): boolean {
    return this.isCryptoReady();
  }

  /**
   * Get the user ID mapping for debugging (dev mode only).
   * Returns a map of hashed user IDs to original user IDs.
   */
  getUserIdMap(): ReadonlyMap<string, string> {
    return EncryptedStorageService.userIdMap;
  }

  /**
   * Get current session info for debugging (dev mode only).
   * Returns null if not initialized.
   */
  getCurrentSessionInfo(): {
    userId: string;
    hashedUserId: string;
    localCacheSize: number;
    sessionCacheSize: number;
  } | null {
    if (!this.userId || !this.hashedUserId) {
      return null;
    }
    return {
      userId: this.userId,
      hashedUserId: this.hashedUserId,
      localCacheSize: Object.keys(this.localCache).length,
      sessionCacheSize: Object.keys(this.sessionCache).length,
    };
  }

  /**
   * Register a new allowed key at runtime.
   * Returns true if the key was added, false if it already existed.
   */
  registerKey(key: string): boolean {
    if (this.allowedKeys.has(key)) {
      return false;
    }
    this.allowedKeys.add(key);
    return true;
  }

  /**
   * Check if a key is allowed.
   */
  isKeyAllowed(key: string): boolean {
    return this.allowedKeys.has(key);
  }

  /**
   * Get all allowed keys.
   */
  getAllowedKeys(): readonly string[] {
    return [...this.allowedKeys];
  }

  /**
   * Store a value in the cache.
   * Call save() to persist changes to storage.
   * @param options - Storage persistence options such as storage type and expiration.
   */
  setItem<T>(key: string, value: T, options?: StorageOptions): void {
    this.ensureInitialized();
    this.ensureKeyAllowed(key);

    const storageType = options?.storageType ?? 'local';
    const cache = this.getCache(storageType);

    cache[key] = {
      value,
      expiresAt: options?.expiresIn ? Date.now() + options.expiresIn : undefined,
    };
    this.setDirty(storageType, true);
  }

  /**
   * Retrieve a value from the cache.
   * Returns null if the item doesn't exist or has expired.
   * @param storageType - 'local' (default) or 'session'
   */
  getItem<T>(key: string, storageType: StorageType = 'local'): T | null {
    this.ensureInitialized();
    this.ensureKeyAllowed(key);

    const cache = this.getCache(storageType);
    const storedItem = cache[key] as StoredItem<T> | undefined;
    if (!storedItem) {
      return null;
    }

    // Check expiration
    if (storedItem.expiresAt && Date.now() > storedItem.expiresAt) {
      this.removeItem(key, storageType);
      return null;
    }

    return storedItem.value;
  }

  /**
   * Remove an item from the cache.
   * Call save() to persist changes to storage.
   * @param storageType - 'local' (default) or 'session'
   */
  removeItem(key: string, storageType: StorageType = 'local'): void {
    this.ensureInitialized();
    this.ensureKeyAllowed(key);

    const cache = this.getCache(storageType);
    if (key in cache) {
      delete cache[key];
      this.setDirty(storageType, true);
    }
  }

  /**
   * Check if any cache has unsaved changes.
   * @param storageType - If provided, check only that storage type. Otherwise check both.
   */
  hasUnsavedChanges(storageType?: StorageType): boolean {
    if (storageType === 'local') {
      return this.isLocalDirty;
    }
    if (storageType === 'session') {
      return this.isSessionDirty;
    }
    return this.isLocalDirty || this.isSessionDirty;
  }

  /**
   * Save cached items to storage as encrypted chunks.
   * @param storageType - If provided, save only that storage type. Otherwise save both.
   */
  async save(storageType?: StorageType): Promise<void> {
    this.ensureInitialized();

    if (!storageType || storageType === 'local') {
      await this.saveStorage('local');
    }
    if (!storageType || storageType === 'session') {
      await this.saveStorage('session');
    }
  }

  private async saveStorage(storageType: StorageType): Promise<void> {
    // Clean expired items before saving
    this.cleanExpiredItems(storageType);

    const cache = this.getCache(storageType);
    const storage = this.getStorage(storageType);
    const encrypted = await this.encrypt(JSON.stringify(cache));
    storage.setItem(this.getDataKey(storageType), JSON.stringify(encrypted));
    this.setDirty(storageType, false);
  }

  /**
   * Convenience method: set an item and immediately save.
   */
  async setItemAndSave<T>(key: string, value: T, options?: StorageOptions): Promise<void> {
    this.setItem(key, value, options);
    const storageType = options?.storageType ?? 'local';
    await this.save(storageType);
  }

  /**
   * Convenience method: remove an item and immediately save.
   * @param storageType - 'local' (default) or 'session'
   */
  async removeItemAndSave(key: string, storageType: StorageType = 'local'): Promise<void> {
    this.removeItem(key, storageType);
    await this.save(storageType);
  }

  /**
   * Clear all items for the current user from cache and storage.
   * @param storageType - If provided, clear only that storage type. Otherwise clear both.
   */
  async clear(storageType?: StorageType): Promise<void> {
    this.ensureInitialized();

    if (!storageType || storageType === 'local') {
      this.localCache = {};
      this.isLocalDirty = false;
      localStorage.removeItem(this.getDataKey('local'));
    }
    if (!storageType || storageType === 'session') {
      this.sessionCache = {};
      this.isSessionDirty = false;
      sessionStorage.removeItem(this.getDataKey('session'));
    }
  }

  /**
   * Clear encryption key from memory (logout scenario).
   * Automatically saves any pending changes before locking.
   */
  async lock(): Promise<void> {
    if ((this.isLocalDirty || this.isSessionDirty) && this.isCryptoReady()) {
      await this.save();
    }
    this.clearCryptoKey();
    this.userId = null;
    this.hashedUserId = null;
    this.localCache = {};
    this.sessionCache = {};
    this.isLocalDirty = false;
    this.isSessionDirty = false;
    this._initialized$.next(false);
  }

  /**
   * Completely reset storage including salt (requires new password on next init).
   * Only removes keys created by this service to avoid affecting other app data.
   */
  reset(): void {
    this.clearServiceKeys(localStorage);
    this.clearServiceKeys(sessionStorage);
    this.clearCryptoKey();
    this.userId = null;
    this.hashedUserId = null;
    this.localCache = {};
    this.sessionCache = {};
    this.isLocalDirty = false;
    this.isSessionDirty = false;
    this._initialized$.next(false);
  }

  /**
   * Remove only keys created by this service from the given storage.
   */
  private clearServiceKeys(storage: Storage): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(this.SALT_KEY_PREFIX) || key?.startsWith(this.DATA_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => storage.removeItem(key));
  }

  /**
   * Type guard to check if a value is a valid TokenClaims object.
   */
  isTokenClaims(value: unknown): value is TokenClaims {
    return typeof value === 'object' && value !== null;
  }

  /**
   * Safely extract a string claim from token data with runtime validation.
   */
  getStringClaim(userData: unknown, claim: keyof TokenClaims): string | undefined {
    if (!this.isTokenClaims(userData)) {
      return undefined;
    }
    const value = userData[claim];
    return typeof value === 'string' ? value : undefined;
  }

  /**
   * Get a snapshot of cached items (for debugging).
   * @param storageType - 'local' (default) or 'session'
   */
  getCacheSnapshot(storageType: StorageType = 'local'): Readonly<CachedStorage> {
    this.ensureInitialized();
    return { ...this.getCache(storageType) };
  }

  private ensureInitialized(): void {
    if (!this.isCryptoReady() || !this.userId || !this.hashedUserId) {
      throw new Error('EncryptedStorageService not initialized. Call initialize() first.');
    }
  }

  private ensureKeyAllowed(key: string): void {
    if (!this.allowedKeys.has(key)) {
      throw new Error(
        `Storage key "${key}" is not allowed. ` +
          `Use registerKey("${key}") to add it, or use one of: ${[...this.allowedKeys].join(', ')}`
      );
    }
  }

  private getCache(storageType: StorageType): CachedStorage {
    return storageType === 'session' ? this.sessionCache : this.localCache;
  }

  private getStorage(storageType: StorageType): Storage {
    return storageType === 'session' ? sessionStorage : localStorage;
  }

  private setDirty(storageType: StorageType, value: boolean): void {
    if (storageType === 'session') {
      this.isSessionDirty = value;
    } else {
      this.isLocalDirty = value;
    }
  }

  private getDataKey(storageType: StorageType): string {
    const suffix = storageType === 'session' ? '__session' : '__local';
    return `${this.DATA_KEY_PREFIX}${this.hashedUserId}${suffix}`;
  }

  private async loadCache(storageType: StorageType): Promise<void> {
    const storage = this.getStorage(storageType);
    const dataKey = this.getDataKey(storageType);
    const storedValue = storage.getItem(dataKey);

    if (!storedValue) {
      if (storageType === 'session') {
        this.sessionCache = {};
      } else {
        this.localCache = {};
      }
      return;
    }

    try {
      const encryptedData: EncryptedData = JSON.parse(storedValue);
      const decrypted = await this.decrypt(encryptedData);
      const cache = JSON.parse(decrypted) as CachedStorage;

      if (storageType === 'session') {
        this.sessionCache = cache;
      } else {
        this.localCache = cache;
      }

      // Clean expired items on load
      this.cleanExpiredItems(storageType);
      this.setDirty(storageType, false);
    } catch (error) {
      // Decryption failed - likely due to encryption library update,
      // corrupted data, or password/key change. Clear corrupted data
      // and start fresh. Mark as dirty so next save overwrites old data.
      if (isDevMode()) {
        console.warn(
          `[EncryptedStorageService] Failed to decrypt ${storageType} storage data. ` +
            `Clearing corrupted data and starting fresh.`,
          error
        );
      }

      // Clear corrupted data from storage
      storage.removeItem(dataKey);

      // Initialize empty cache
      if (storageType === 'session') {
        this.sessionCache = {};
      } else {
        this.localCache = {};
      }

      // Mark as dirty so new data will be saved, overwriting any remnants
      this.setDirty(storageType, true);
    }
  }

  private cleanExpiredItems(storageType: StorageType): void {
    const now = Date.now();
    let hasExpired = false;
    const cache = this.getCache(storageType);

    for (const key of Object.keys(cache)) {
      const item = cache[key];
      if (item?.expiresAt && now > item.expiresAt) {
        delete cache[key];
        hasExpired = true;
      }
    }

    if (hasExpired) {
      this.setDirty(storageType, true);
    }
  }

  /**
   * Get environment info suffix to differentiate storage between environments.
   * Returns empty string for dev/test/localhost, '__stage' for staging, '__prod' for production.
   */
  private getEnvInfo(): string {
    const global = globalThis as typeof globalThis & {
      location: Location;
      config?: { isDev?: boolean; isTest?: boolean; isStage?: boolean };
    };
    const isLocalhost = global.location.href.includes('localhost');
    const config = global.config;

    if (isLocalhost || !config) {
      return '';
    }
    if (config.isDev || config.isTest) {
      return '';
    }
    return config.isStage ? '__stage' : '__prod';
  }

  /**
   * Hash the userId to prevent exposing real email addresses in localStorage keys.
   * Uses SHA-256 and returns a hex string.
   */
  private async hashUserId(userId: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(userId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    return Array.from(hashArray)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
}
